<script lang="ts">
	import {
		DeviceGroup,
		ProjectorPowerListener,
		ProjectorPowerManager,
		type DeviceRPi,
	} from "luxedo-data"
	import { MoonIcon, SunIcon } from "luxedo-icons"
	import { Reactivity } from "luxedo-reactivity"
	import { scale } from "svelte/transition"

	interface Props {
		device: DeviceRPi | DeviceGroup
	}

	let { device }: Props = $props()

	$effect.pre(() => {
		Reactivity.useProps(device, ["isOnline"])
	})

	let powerStateListener = $derived(new ProjectorPowerListener(device))
	let powerState = $derived(powerStateListener.powerState)

	let isOffline = $derived(!Reactivity.propOf(device, "isOnline"))
	let isChanging = $state(false)
	let isHovering = $state(false)

	$effect(() => {
		return ProjectorPowerManager.subscribe((ctx) => {
			if (device.id in ctx) {
				isChanging = ctx[device.id].isChanging
				return
			}

			if (device instanceof DeviceGroup) {
				const childrenIds = device.getChildDevices().map((dev) => dev.id)
				for (const id of childrenIds) {
					if (id in ctx && ctx[id].isChanging) {
						isChanging = ctx[device.id].isChanging
						return
					}
				}
			}

			isChanging = false
		})
	})

	let isUnkonwn = $derived(["UNDEFINED", "UNKNOWN"].includes(powerState))
	let isPoweringOn = $derived(["POWERING_ON", "WARMING_UP"].includes(powerState))
	let isPoweringOff = $derived(["POWERING_OFF", "COOLING_DOWN"].includes(powerState))

	let isOn = $derived(powerState === "ON")
	let isOff = $derived(powerState === "OFF" || powerState === "PARTIAL") // handle partially off as off for groups

	let showMoon = $derived(
		(isOff && !isHovering && !isChanging) || isPoweringOff || (isOn && (isChanging || isHovering))
	)

	function toggleProjectorPower(e: MouseEvent) {
		e.stopPropagation()
		ProjectorPowerManager.setPower(device, isOff ? "ON" : "OFF")
	}

	$inspect(powerState)
</script>

<button
	class="icon wake-button"
	class:isHovering
	class:isChanging
	disabled={isPoweringOff || isPoweringOn || isChanging}
	onclick={toggleProjectorPower}
	onmouseover={() => (isHovering = true)}
	onmouseleave={() => (isHovering = false)}
	title={isOff ? "Wake up" : "Go to sleep"}
>
	{#if !isUnkonwn && !isOffline}
		{#if showMoon}
			<div class="icon-container" in:scale={{ start: 0 }} out:scale={{ start: 1 }}>
				<MoonIcon />
			</div>
		{:else}
			<div class="icon-container" in:scale={{ start: 0 }} out:scale={{ start: 1 }}>
				<SunIcon />
			</div>
		{/if}
	{/if}
</button>

<style>
	.wake-button {
		width: 2rem;
		height: 2rem;
		position: relative;

		--color-fill: var(--color-text);
	}

	.wake-button :global(.svg-fill) {
		fill: var(--color-fill);
	}

	.wake-button:hover :global(.svg-fill),
	.wake-button:focus-visible :global(.svg-fill) {
		--color-fill: var(--color-text-light);
	}

	.icon-container {
		position: absolute;
		width: fit-content;
	}

	.wake-button:disabled {
		opacity: 1;
	}

	@keyframes flashing {
		0% {
			opacity: 1;
			/* --color-fill: var(--color-text); */
		}

		50% {
			opacity: 0.5;
			/* --color-fill: var(--color-text-light); */
		}

		100% {
			opacity: 1;
			/* --color-fill: var(--color-text); */
		}
	}

	.isChanging :global(.svg-fill) {
		animation-name: flashing;
		animation-duration: 2s;
		animation-iteration-count: infinite;
	}
</style>
